import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { submissionsManagementService } from '../../services/submissionsManagementService';

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, sub, color = 'text-primary' }) => (
  <div className="bg-card border border-border rounded-xl p-5 flex items-start gap-4">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${color === 'text-primary' ? 'bg-primary/10' : color === 'text-emerald-600' ? 'bg-emerald-50' : color === 'text-rose-600' ? 'bg-rose-50' : 'bg-amber-50'}`}>
      <Icon name={icon} size={20} className={color} />
    </div>
    <div>
      <p className="text-2xl font-bold text-foreground leading-tight">{value}</p>
      <p className="text-sm font-medium text-foreground/80">{label}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    active:      { label: 'Active',      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    unsubscribed:{ label: 'Unsubscribed',cls: 'bg-rose-50 text-rose-700 border-rose-200' },
    pending:     { label: 'Pending',     cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    compliant:   { label: 'Compliant',   cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    violation:   { label: 'Violation',   cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  };
  const s = map?.[status] || map?.pending;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${s?.cls}`}>
      {s?.label}
    </span>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const A2PConsentManagement = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [activeTab, setActiveTab] = useState('consents');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterForm, setFilterForm] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);

  // Mock consent data derived from real form submissions concept
  const [consents] = useState([
    { id: 1, name: 'Sarah Johnson', phone: '+1 (555) 234-5678', email: 'sarah.j@email.com', form: 'Contact Us Form', consentText: 'I consent to receive non-marketing text messages...', timestamp: '2026-08-05 14:32', status: 'active', ipAddress: '192.168.1.1', source: 'Web Form' },
    { id: 2, name: 'Michael Torres', phone: '+1 (555) 876-5432', email: 'm.torres@email.com', form: 'Appointment Booking', consentText: 'I consent to receive non-marketing text messages...', timestamp: '2026-08-05 13:15', status: 'active', ipAddress: '10.0.0.45', source: 'Web Form' },
    { id: 3, name: 'Emily Chen', phone: '+1 (555) 345-6789', email: 'emily.c@email.com', form: 'Order Updates Form', consentText: 'I consent to receive non-marketing text messages...', timestamp: '2026-08-04 09:47', status: 'unsubscribed', ipAddress: '172.16.0.8', source: 'Web Form' },
    { id: 4, name: 'David Park', phone: '+1 (555) 654-3210', email: 'd.park@email.com', form: 'Contact Us Form', consentText: 'I consent to receive non-marketing text messages...', timestamp: '2026-08-04 08:22', status: 'active', ipAddress: '192.168.2.5', source: 'Web Form' },
    { id: 5, name: 'Lisa Martinez', phone: '+1 (555) 789-0123', email: 'lisa.m@email.com', form: 'Appointment Booking', consentText: 'I consent to receive non-marketing text messages...', timestamp: '2026-08-03 16:55', status: 'active', ipAddress: '10.0.1.22', source: 'Web Form' },
    { id: 6, name: 'James Wilson', phone: '+1 (555) 012-3456', email: 'j.wilson@email.com', form: 'Order Updates Form', consentText: 'I consent to receive non-marketing text messages...', timestamp: '2026-08-03 11:30', status: 'unsubscribed', ipAddress: '192.168.3.9', source: 'Web Form' },
    { id: 7, name: 'Amanda Foster', phone: '+1 (555) 321-6540', email: 'a.foster@email.com', form: 'Contact Us Form', consentText: 'I consent to receive non-marketing text messages...', timestamp: '2026-08-02 15:10', status: 'active', ipAddress: '172.16.1.3', source: 'Web Form' },
    { id: 8, name: 'Robert Kim', phone: '+1 (555) 456-7890', email: 'r.kim@email.com', form: 'Appointment Booking', consentText: 'I consent to receive non-marketing text messages...', timestamp: '2026-08-01 10:05', status: 'pending', ipAddress: '10.0.2.14', source: 'Web Form' },
  ]);

  const [unsubscribes] = useState([
    { id: 1, name: 'Emily Chen', phone: '+1 (555) 345-6789', form: 'Order Updates Form', unsubscribeDate: '2026-08-05 10:12', method: 'STOP Reply', originalConsent: '2026-08-04 09:47' },
    { id: 2, name: 'James Wilson', phone: '+1 (555) 012-3456', form: 'Order Updates Form', unsubscribeDate: '2026-08-04 14:30', method: 'STOP Reply', originalConsent: '2026-08-03 11:30' },
    { id: 3, name: 'Patricia Lee', phone: '+1 (555) 567-8901', form: 'Contact Us Form', unsubscribeDate: '2026-08-02 09:00', method: 'Manual Opt-Out', originalConsent: '2026-07-28 12:00' },
  ]);

  const [complianceData] = useState([
    { id: 1, form: 'Contact Us Form', totalConsents: 45, activeSubscribers: 38, unsubscribes: 7, optOutRate: '15.6%', consentLanguage: 'Present', stopKeyword: 'Enabled', helpKeyword: 'Enabled', status: 'compliant' },
    { id: 2, form: 'Appointment Booking', totalConsents: 32, activeSubscribers: 29, unsubscribes: 3, optOutRate: '9.4%', consentLanguage: 'Present', stopKeyword: 'Enabled', helpKeyword: 'Enabled', status: 'compliant' },
    { id: 3, form: 'Order Updates Form', totalConsents: 28, activeSubscribers: 21, unsubscribes: 7, optOutRate: '25.0%', consentLanguage: 'Present', stopKeyword: 'Enabled', helpKeyword: 'Enabled', status: 'compliant' },
    { id: 4, form: 'Newsletter Signup', totalConsents: 15, activeSubscribers: 15, unsubscribes: 0, optOutRate: '0%', consentLanguage: 'Missing', stopKeyword: 'Disabled', helpKeyword: 'Disabled', status: 'violation' },
  ]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/authentication'); return; }
    const loadForms = async () => {
      try {
        const data = await submissionsManagementService?.getUserForms?.();
        setForms(data || []);
      } catch {
        setForms([]);
      } finally {
        setLoading(false);
      }
    };
    loadForms();
  }, [user, authLoading, navigate]);

  const tabs = [
    { id: 'consents',    label: 'Collected Consents', icon: 'CheckSquare' },
    { id: 'subscribers', label: 'Subscriber Lists',   icon: 'Users' },
    { id: 'unsubscribes',label: 'Unsubscribes',       icon: 'UserMinus' },
    { id: 'compliance',  label: 'Opt-Out Compliance', icon: 'ShieldCheck' },
  ];

  const uniqueForms = [...new Set(consents?.map(c => c?.form))];

  const filteredConsents = consents?.filter(c => {
    const matchSearch = !searchTerm || c?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) || c?.phone?.includes(searchTerm) || c?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchForm   = filterForm === 'all' || c?.form === filterForm;
    const matchStatus = filterStatus === 'all' || c?.status === filterStatus;
    return matchSearch && matchForm && matchStatus;
  });

  const activeCount      = consents?.filter(c => c?.status === 'active')?.length;
  const unsubscribeCount = consents?.filter(c => c?.status === 'unsubscribed')?.length;
  const complianceRate   = Math.round((complianceData?.filter(f => f?.status === 'compliant')?.length / complianceData?.length) * 100);

  const handleExportCSV = () => {
    const rows = [
      ['Name', 'Phone', 'Email', 'Form', 'Timestamp', 'Status', 'IP Address'],
      ...filteredConsents?.map(c => [c?.name, c?.phone, c?.email, c?.form, c?.timestamp, c?.status, c?.ipAddress])
    ];
    const csv = rows?.map(r => r?.join(','))?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL?.createObjectURL(blob);
    const a    = document?.createElement('a');
    a.href = url; a.download = 'a2p_consents.csv'; a?.click();
    URL?.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
          <p className="text-muted-foreground text-sm">Loading consent data…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar stats={{
        totalConsents: consents?.length,
        activeSubscribers: activeCount,
        unsubscribes: unsubscribeCount,
        complianceRate: `${complianceRate}%`
      }} />
      <div className="pt-16" style={{ paddingLeft: 'var(--sidebar-width, 256px)', transition: 'padding-left 0.3s ease' }}>
        <div className="max-w-7xl mx-auto px-6 py-8">

          {/* Header */}
          <div className="flex items-start justify-between mb-8">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Icon name="ShieldCheck" size={22} className="text-primary" />
                <h1 className="text-2xl font-bold text-foreground">A2P Consent Management</h1>
              </div>
              <p className="text-muted-foreground text-sm">
                View collected SMS consents, manage subscriber lists, track unsubscribes, and monitor opt-out compliance across all forms.
              </p>
            </div>
            <Button variant="outline" size="sm" iconName="Download" iconPosition="left" onClick={handleExportCSV}>
              Export CSV
            </Button>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon="CheckSquare" label="Total Consents"      value={consents?.length}   sub="All time"             color="text-primary" />
            <StatCard icon="Users"       label="Active Subscribers"  value={activeCount}        sub="Currently opted-in"   color="text-emerald-600" />
            <StatCard icon="UserMinus"   label="Unsubscribes"        value={unsubscribeCount}   sub="Opted out via STOP"   color="text-rose-600" />
            <StatCard icon="ShieldCheck" label="Compliance Rate"     value={`${complianceRate}%`} sub="Forms with valid A2P" color="text-amber-600" />
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-muted/50 rounded-xl p-1 mb-6 w-fit">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${activeTab === tab?.id ? 'bg-card text-foreground shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <Icon name={tab?.icon} size={15} />
                {tab?.label}
              </button>
            ))}
          </div>

          {/* ── CONSENTS TAB ── */}
          {activeTab === 'consents' && (
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              {/* Filters */}
              <div className="px-5 py-4 border-b border-border flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 min-w-[200px]">
                  <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search by name, phone, or email…"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e?.target?.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
                  />
                </div>
                <select
                  value={filterForm}
                  onChange={e => setFilterForm(e?.target?.value)}
                  className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
                >
                  <option value="all">All Forms</option>
                  {uniqueForms?.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <select
                  value={filterStatus}
                  onChange={e => setFilterStatus(e?.target?.value)}
                  className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="unsubscribed">Unsubscribed</option>
                  <option value="pending">Pending</option>
                </select>
                <span className="text-xs text-muted-foreground ml-auto">{filteredConsents?.length} record{filteredConsents?.length !== 1 ? 's' : ''}</span>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Subscriber</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Phone</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Source Form</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Consent Date</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">IP Address</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredConsents?.map(c => (
                      <tr key={c?.id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-5 py-3.5">
                          <div>
                            <p className="font-medium text-foreground">{c?.name}</p>
                            <p className="text-xs text-muted-foreground">{c?.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-foreground font-mono text-xs">{c?.phone}</td>
                        <td className="px-4 py-3.5">
                          <span className="inline-flex items-center gap-1.5 text-xs bg-primary/8 text-primary px-2 py-1 rounded-md font-medium">
                            <Icon name="FileText" size={11} />
                            {c?.form}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-muted-foreground text-xs">{c?.timestamp}</td>
                        <td className="px-4 py-3.5 text-muted-foreground font-mono text-xs">{c?.ipAddress}</td>
                        <td className="px-4 py-3.5"><StatusBadge status={c?.status} /></td>
                      </tr>
                    ))}
                    {filteredConsents?.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-5 py-12 text-center text-muted-foreground text-sm">
                          <Icon name="SearchX" size={32} className="mx-auto mb-2 opacity-30" />
                          No consents match your filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── SUBSCRIBERS TAB ── */}
          {activeTab === 'subscribers' && (
            <div className="space-y-4">
              {uniqueForms?.map(formName => {
                const subs = consents?.filter(c => c?.form === formName && c?.status === 'active');
                return (
                  <div key={formName} className="bg-card border border-border rounded-xl overflow-hidden">
                    <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Icon name="FileText" size={16} className="text-primary" />
                        </div>
                        <div>
                          <p className="font-semibold text-foreground text-sm">{formName}</p>
                          <p className="text-xs text-muted-foreground">{subs?.length} active subscriber{subs?.length !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      <Button variant="outline" size="xs" iconName="Download" iconPosition="left" onClick={() => {}}>
                        Export List
                      </Button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border bg-muted/30">
                            <th className="text-left px-5 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Name</th>
                            <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Phone</th>
                            <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Email</th>
                            <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Subscribed</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {subs?.map(s => (
                            <tr key={s?.id} className="hover:bg-muted/20 transition-colors">
                              <td className="px-5 py-3 font-medium text-foreground">{s?.name}</td>
                              <td className="px-4 py-3 font-mono text-xs text-foreground">{s?.phone}</td>
                              <td className="px-4 py-3 text-muted-foreground text-xs">{s?.email}</td>
                              <td className="px-4 py-3 text-muted-foreground text-xs">{s?.timestamp}</td>
                            </tr>
                          ))}
                          {subs?.length === 0 && (
                            <tr>
                              <td colSpan={4} className="px-5 py-8 text-center text-muted-foreground text-sm">No active subscribers for this form.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── UNSUBSCRIBES TAB ── */}
          {activeTab === 'unsubscribes' && (
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                <div>
                  <p className="font-semibold text-foreground">Unsubscribe Log</p>
                  <p className="text-xs text-muted-foreground mt-0.5">All opt-outs recorded via STOP reply or manual removal</p>
                </div>
                <span className="text-xs font-medium text-rose-600 bg-rose-50 border border-rose-200 px-2.5 py-1 rounded-full">{unsubscribes?.length} total opt-outs</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Subscriber</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Phone</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Source Form</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Opt-Out Method</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Unsubscribed</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Original Consent</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {unsubscribes?.map(u => (
                      <tr key={u?.id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-5 py-3.5 font-medium text-foreground">{u?.name}</td>
                        <td className="px-4 py-3.5 font-mono text-xs text-foreground">{u?.phone}</td>
                        <td className="px-4 py-3.5">
                          <span className="inline-flex items-center gap-1.5 text-xs bg-primary/8 text-primary px-2 py-1 rounded-md font-medium">
                            <Icon name="FileText" size={11} />
                            {u?.form}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${u?.method === 'STOP Reply' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                            <Icon name={u?.method === 'STOP Reply' ? 'MessageSquareX' : 'UserX'} size={11} />
                            {u?.method}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-muted-foreground text-xs">{u?.unsubscribeDate}</td>
                        <td className="px-4 py-3.5 text-muted-foreground text-xs">{u?.originalConsent}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── COMPLIANCE TAB ── */}
          {activeTab === 'compliance' && (
            <div className="space-y-4">
              {/* Compliance summary banner */}
              <div className={`rounded-xl border px-5 py-4 flex items-start gap-4 ${complianceRate === 100 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                <Icon name={complianceRate === 100 ? 'ShieldCheck' : 'AlertTriangle'} size={22} className={complianceRate === 100 ? 'text-emerald-600 mt-0.5' : 'text-amber-600 mt-0.5'} />
                <div>
                  <p className={`font-semibold text-sm ${complianceRate === 100 ? 'text-emerald-800' : 'text-amber-800'}`}>
                    {complianceRate === 100 ? 'All forms are A2P compliant.' : `${complianceData?.filter(f => f?.status === 'violation')?.length} form(s) have compliance issues.`}
                  </p>
                  <p className={`text-xs mt-0.5 ${complianceRate === 100 ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {complianceRate}% of your forms include proper A2P consent language, STOP keyword support, and HELP keyword support.
                  </p>
                </div>
              </div>

              {/* Per-form compliance table */}
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-border">
                  <p className="font-semibold text-foreground text-sm">Per-Form Compliance Status</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Checks for consent language, STOP keyword, and HELP keyword across all forms with A2P fields</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Form</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Total Consents</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Active</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Opt-Out Rate</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Consent Language</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">STOP Keyword</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">HELP Keyword</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {complianceData?.map(f => (
                        <tr key={f?.id} className="hover:bg-muted/20 transition-colors">
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-2">
                              <Icon name="FileText" size={14} className="text-primary flex-shrink-0" />
                              <span className="font-medium text-foreground">{f?.form}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3.5 text-foreground">{f?.totalConsents}</td>
                          <td className="px-4 py-3.5 text-emerald-600 font-medium">{f?.activeSubscribers}</td>
                          <td className="px-4 py-3.5">
                            <span className={`text-sm font-medium ${parseFloat(f?.optOutRate) > 20 ? 'text-rose-600' : 'text-foreground'}`}>{f?.optOutRate}</span>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${f?.consentLanguage === 'Present' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                              <Icon name={f?.consentLanguage === 'Present' ? 'Check' : 'X'} size={11} />
                              {f?.consentLanguage}
                            </span>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${f?.stopKeyword === 'Enabled' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                              <Icon name={f?.stopKeyword === 'Enabled' ? 'Check' : 'X'} size={11} />
                              {f?.stopKeyword}
                            </span>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${f?.helpKeyword === 'Enabled' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                              <Icon name={f?.helpKeyword === 'Enabled' ? 'Check' : 'X'} size={11} />
                              {f?.helpKeyword}
                            </span>
                          </td>
                          <td className="px-4 py-3.5"><StatusBadge status={f?.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* A2P Requirements Checklist */}
              <div className="bg-card border border-border rounded-xl p-5">
                <p className="font-semibold text-foreground text-sm mb-4">A2P 10DLC Compliance Checklist</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { label: 'Explicit written consent collected before sending messages', done: true },
                    { label: 'Consent language includes company name and message type', done: true },
                    { label: 'Message & data rates disclosure present', done: true },
                    { label: 'STOP keyword opt-out instruction included', done: true },
                    { label: 'HELP keyword support instruction included', done: true },
                    { label: 'Consent records stored with timestamp and IP', done: true },
                    { label: 'Opt-out requests honored within 24 hours', done: true },
                    { label: 'All forms with A2P fields reviewed for compliance', done: complianceRate === 100 },
                  ]?.map((item, i) => (
                    <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${item?.done ? 'bg-emerald-50/50 border-emerald-100' : 'bg-rose-50/50 border-rose-100'}`}>
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${item?.done ? 'bg-emerald-500' : 'bg-rose-400'}`}>
                        <Icon name={item?.done ? 'Check' : 'X'} size={11} color="white" />
                      </div>
                      <p className={`text-xs leading-relaxed ${item?.done ? 'text-emerald-800' : 'text-rose-800'}`}>{item?.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default A2PConsentManagement;
